import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BikResponse, BikValidationResult } from "@/types/payroll-validator-demo";

type Props = {
  response: BikResponse | null;
};

const RULE_LABELS: Record<string, string> = {
  BIK_NOT_IN_TAXABLE: "Benefits missing from taxable gross pay",
};

const formatRuleCode = (code?: string | null) => {
  if (!code) return "Rule";
  return RULE_LABELS[code] ?? code.replace(/_/g, " ").toLowerCase();
};

const formatCurrency = (value?: string | number | null) => {
  if (value === null || value === undefined) return "—";
  const asNumber = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(asNumber)) return String(value);
  return `£${asNumber.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const BIKValidationResults: React.FC<Props> = ({ response }) => {
  const hasFindings = (response?.results?.length || 0) > 0;

  if (!response) return null;

  return (
    <div className="space-y-3 pt-2">
      <Alert
        variant="default"
        className={
          hasFindings
            ? "border border-orange-500/30 bg-orange-500/10 text-amber-100"
            : "border border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
        }
      >
        <AlertTitle className={hasFindings ? "text-orange-50" : "text-emerald-50"}>
          {hasFindings ? "Findings detected" : "No issues found"}
        </AlertTitle>
        <AlertDescription
          className={hasFindings ? "text-orange-100/90" : "text-emerald-100/90"}
        >
          {hasFindings
            ? `${response.summary.total} finding${response.summary.total === 1 ? "" : "s"} (${response.summary.critical} critical)`
            : "No gaps between benefits and taxable gross were detected."}
        </AlertDescription>
      </Alert>

      {hasFindings &&
        response.results.map((result, index) => (
          <BIKResultCard key={`${result.rule_code}-${index}`} result={result} />
        ))}
    </div>
  );
};

type ResultCardProps = {
  result: BikValidationResult;
};

const BIKResultCard: React.FC<ResultCardProps> = ({ result }) => (
  <Card className="border border-orange-500/20 bg-[#0f1117] text-slate-100">
    <CardContent className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="text-sm text-slate-400">
            {result.employee_name || "Employee"}{" "}
            {result.ni_number ? `· ${result.ni_number}` : ""}
          </p>
          <p className="text-base font-semibold text-white">
            {formatRuleCode(result.rule_code)}
          </p>
        </div>
        <Badge
          variant="outline"
          className={
            result.severity === "critical"
              ? "border-red-400/40 bg-red-500/15 text-red-100"
              : "border-amber-300/40 bg-amber-400/15 text-amber-50"
          }
        >
          {result.severity}
        </Badge>
      </div>
      {result.description && <p className="text-sm text-slate-200">{result.description}</p>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <InfoRow label="BIK total" value={formatCurrency(result.bik_total)} />
        <InfoRow label="Taxable gross" value={formatCurrency(result.taxable_gross_pay)} />
        <InfoRow label="Expected taxable" value={formatCurrency(result.expected_taxable_gross_pay)} />
        <InfoRow
          label="Difference"
          value={formatCurrency(result.difference)}
          valueClass="text-red-200"
        />
      </div>
    </CardContent>
  </Card>
);

type InfoRowProps = {
  label: string;
  value: string;
  valueClass?: string;
};

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueClass }) => (
  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
    <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
    <span className={`text-sm font-semibold text-slate-100 ${valueClass ?? ""}`}>{value}</span>
  </div>
);

export default BIKValidationResults;

